import React from "react";
import * as Sentry from "@sentry/react";
import { create } from "zustand";
import UserData from "../models/UserData";
import Auth from "../Auth";
import _ from "lodash";
import { fetchInstalledPluginsList } from "../hooks/installedPluginsListCache";

const READ_RESPONSE_RETRY_OPTIONS = {
  responseReadFailureRetries: 2,
  responseReadFailureRetryDelayMs: 250,
};

const initialState = {
  editorOptions: UserData.defaultValues(),
  userData: null,
  subscription: null,
  subscriptionJSON: null,
  installedPlugins: [],
  settingsOpen: false,
  isStyleSettingsOpen: false,
  isStyleSettingsNestedModalOpen: false,
  styleSettingsActiveCategory: null,
  styleSettingsFocusStyleId: null,
  styleSettingsFocusAdvanced: false,
  demo: false,
  needsImport: false,
  hasImportedOrSkipped: false,
  helpOpen: false,
  storyBibleTitleExpand: true,
  customModels: [],
  subscribeModalOpenedFrom: null,
  isInUnlimitedExperiment: false,
  unlimitedExperimentVariation: null,
  // Draft defaults persisted across devices (source of truth in Zustand + Firestore)
  draftDefaults: {
    lastUsedDraftModelGlobal: null,
    lastUsedDraftCreativityGlobal: null,
  },
};

export const useSudowriteStore = create((set, get) => ({
  ...initialState,

  setEditorOptions: (editorOptions) => {
    set((state) => ({
      editorOptions,
      draftDefaults: {
        ...state.draftDefaults,
        lastUsedDraftModelGlobal: editorOptions?.lastUsedDraftModelGlobal ?? null,
      },
    }));
  },

  setUserData: (userData) => {
    const accountChanged = !userData || get().userData?.userId !== userData.userId;
    if (accountChanged) {
      // Account preferences must not survive a sign-out or account switch,
      // including the interval before the new account's document loads.
      set({
        userData,
        editorOptions: { ...UserData.defaultValues(), ...userData?.data },
        draftDefaults: {
          lastUsedDraftModelGlobal: userData?.data?.lastUsedDraftModelGlobal ?? null,
          lastUsedDraftCreativityGlobal: userData?.data?.lastUsedDraftCreativityGlobal ?? null,
        },
      });
      return;
    }
    set({ userData });

    if (userData?.data) {
      set((state) => {
        const updates = {};

        if (state.draftDefaults.lastUsedDraftModelGlobal === null && userData.data.lastUsedDraftModelGlobal) {
          updates.lastUsedDraftModelGlobal = userData.data.lastUsedDraftModelGlobal;
        }

        if (state.draftDefaults.lastUsedDraftCreativityGlobal === null && userData.data.lastUsedDraftCreativityGlobal !== undefined) {
          updates.lastUsedDraftCreativityGlobal = userData.data.lastUsedDraftCreativityGlobal;
        }

        if (Object.keys(updates).length > 0) {
          return { draftDefaults: { ...state.draftDefaults, ...updates } };
        }

        return {};
      });
    }
  },

  setSubscription: (subscription) => set({ subscription }),

  setSubscriptionJSON: (subscriptionJSON) => set({ subscriptionJSON }),

  setInstalledPlugins: (installedPlugins) => set({ installedPlugins }),

  setCustomModels: (customModels) => set({ customModels }),

  setSettingsOpen: (settingsOpen) => set({ settingsOpen }),

  openStyleSettings: (activeCategory = null, focusStyleIdOrOptions = null) => {
    let styleSettingsFocusStyleId = null;
    let styleSettingsFocusAdvanced = false;

    if (typeof focusStyleIdOrOptions === "string") {
      styleSettingsFocusStyleId = focusStyleIdOrOptions;
    } else if (focusStyleIdOrOptions && typeof focusStyleIdOrOptions === "object") {
      styleSettingsFocusStyleId = focusStyleIdOrOptions.focusStyleId ?? null;
      styleSettingsFocusAdvanced = !!focusStyleIdOrOptions.advanced;
    }

    set({
      isStyleSettingsOpen: true,
      styleSettingsActiveCategory: activeCategory,
      styleSettingsFocusStyleId,
      styleSettingsFocusAdvanced,
    });
  },
  clearStyleSettingsFocusStyle: () => set({ styleSettingsFocusStyleId: null }),
  clearStyleSettingsFocusAdvanced: () => set({ styleSettingsFocusAdvanced: false }),
  setStyleSettingsNestedModalOpen: (isStyleSettingsNestedModalOpen) =>
    set({ isStyleSettingsNestedModalOpen: !!isStyleSettingsNestedModalOpen }),
  closeStyleSettings: () =>
    set({
      isStyleSettingsOpen: false,
      styleSettingsActiveCategory: null,
      styleSettingsFocusStyleId: null,
      styleSettingsFocusAdvanced: false,
      isStyleSettingsNestedModalOpen: false,
    }),

  setDemo: (demo) => set({ demo }),
  setNeedsImport: (needsImport) => set({ needsImport }),
  setHasImportedOrSkipped: (hasImportedOrSkipped) => set({ hasImportedOrSkipped }),
  setHelpOpen: (helpOpen) => set({ helpOpen }),

  setSubscribeModalOpenedFrom: (subscribeModalOpenedFrom) => set({ subscribeModalOpenedFrom }),

  setDraftHighQuality: async (highQuality) => {
    const userData = get().userData;
    set((state) => ({ editorOptions: { ...state.editorOptions, draftHighQuality: highQuality } }));

    try {
      if (userData) {
        await userData.update({ draftHighQuality: highQuality });
      }
    } catch (err) {
      Sentry.captureException(err, { tags: { userId: userData?.userId || "unknown", feature: "draft_high_quality" } });
    }
  },

  setDraftLastUsedModel: async (projectId, modelKey) => {
    set((state) => ({
      draftDefaults: {
        ...state.draftDefaults,
        lastUsedDraftModelGlobal: modelKey,
      },
      editorOptions: { ...state.editorOptions, lastUsedDraftModelGlobal: modelKey, hasChangedDraftDefault: true },
    }));

    try {
      const userData = get().userData;
      if (userData) {
        await userData.update({ lastUsedDraftModelGlobal: modelKey, hasChangedDraftDefault: true });
      }
    } catch (err) {
      const userId = get().userData?.userId;
      Sentry.captureException(err, { tags: { projectId: projectId || "unknown", userId: userId || "unknown", feature: "draft_defaults_model" } });
    }
  },

  setDraftLastUsedCreativity: async (projectId, creativityLevel) => {
    set((state) => ({
      draftDefaults: {
        ...state.draftDefaults,
        lastUsedDraftCreativityGlobal: creativityLevel,
      },
    }));

    try {
      const userData = get().userData;
      if (userData) {
        await userData.update({ lastUsedDraftCreativityGlobal: creativityLevel });
      }
    } catch (err) {
      const userId = get().userData?.userId;
      Sentry.captureException(err, { tags: { projectId: projectId || "unknown", userId: userId || "unknown", feature: "draft_defaults_creativity" } });
    }
  },

  getDraftDefaults: (projectId) => {
    const draftDefaults = get().draftDefaults;
    const data = get().userData?.data || {};
    const byProjectModel = data.lastUsedDraftModelByProject || {};
    const byProjectCreativity = data.lastUsedDraftCreativityByProject || {};

    const modelKey = draftDefaults.lastUsedDraftModelGlobal || (projectId && byProjectModel[projectId]) || undefined;
    const creativity = draftDefaults.lastUsedDraftCreativityGlobal ?? (projectId && byProjectCreativity[projectId]);

    return { modelKey, creativity };
  },

  reset: () => set(initialState),

  fetchInstalledPlugins: async () => {
    const installations = await fetchInstalledPluginsList("installed-plugins-store");
    get().setInstalledPlugins(installations);
  },

  fetchCustomModels: async () => {
    const response = await Auth.fetchWithIdToken("/api/my_voice/fine_tunes", {}, null, {
      ...READ_RESPONSE_RETRY_OPTIONS,
      requestSource: "startup-custom-models",
    });
    get().setCustomModels(response);
  },

  setStoryBibleTitleExpand: (storyBibleTitleExpand) => set({ storyBibleTitleExpand }),

  setIsImportDisabled: (isImportDisabled) => set({ isImportDisabled }),

  getFeatureFlags: () => get().subscription.getFeatureFlags(),

  setIsInUnlimitedExperiment: (isInUnlimitedExperiment) => set({ isInUnlimitedExperiment }),
  setUnlimitedExperimentVariation: (unlimitedExperimentVariation) => set({ unlimitedExperimentVariation }),
}));

export const withSudowriteStore = (BaseComponent) => {
  const MemoizedBaseComponent = React.memo(BaseComponent, (prev, next) => {
    return _.isEqual(prev, next);
  });

  return (props) => {
    const selector = (state) => {
      return {
        installedPlugins: state.installedPlugins,
        settingsOpen: state.settingsOpen,
      };
    };

    const selectedState = useSudowriteStore(selector);

    // Keep this file plain `.js` without JSX so Vitest/Vite SSR can parse it consistently.
    return React.createElement(MemoizedBaseComponent, {
      ...props,
      sudowriteStore: selectedState,
    });
  };
};
